import { initComponent } from './core/init-component';
import { bindAll } from './utils/bind-all';
import { initCursorFollowClose } from './utils/cursor-follow-close';

initComponent('.js-header', (section) => {
  const logoFlip = section.querySelector<HTMLElement>('.js-logo-flip');
  const isMobile = () => window.matchMedia('(max-width: 767px)').matches;

  const DURATION = 2000;
  const DELAY = 5000;

  let flipTimeout: number | undefined;
  let cycleTimeout: number | undefined;

  const wrapper = section.closest('.js-header-wrapper') as HTMLElement;
  const hero = document.querySelector('.js-has-hero') as HTMLElement | null;
  const pdp = document.querySelector('.js-pdp-main') as HTMLElement | null;
  const hasHero = !!hero;
  const isPdp = !!pdp;
  

  let lastScroll = window.scrollY;
  let accumulatedUp = 0;

  // Logo flip

  const clearTimers = () => {
    if (flipTimeout) window.clearTimeout(flipTimeout);
    if (cycleTimeout) window.clearTimeout(cycleTimeout);
  };

  const startFlip = () => {
    if (!logoFlip || !isMobile()) return;

    logoFlip.classList.remove('is-flipping');
    void logoFlip.offsetWidth;
    logoFlip.classList.add('is-flipping');

    flipTimeout = window.setTimeout(() => {
      logoFlip.classList.remove('is-flipping');
      cycle();
    }, DURATION);
  };

  const cycle = () => {
    if (!logoFlip || !isMobile()) return;

    clearTimers();
    cycleTimeout = window.setTimeout(startFlip, DELAY);
  };

  const stop = () => {
    if (!logoFlip) return;
    clearTimers();
    logoFlip.classList.remove('is-flipping');
  };

  const handleActivity = () => {
    if (!isMobile()) {
      stop();
      return;
    }

    stop();
    cycle(); 
  };

  window.addEventListener('touchstart', handleActivity, { passive: true });
  window.addEventListener('scroll', handleActivity, { passive: true });
  window.addEventListener('resize', handleActivity);

  cycle();

  const setInitialState = () => {
    if (hasHero) {
      section.classList.add('absolute', 'top-0', 'left-0');
      section.classList.remove('relative');
    } else {
      section.classList.add('relative');
      if (wrapper) {
        wrapper.style.height = `${section.offsetHeight}px`;
      }
    }

    hidePdpHeaderBehindClone();

    section.classList.add(
      'transition-all',
      'duration-[300ms]',
      'ease-[cubic-bezier(0.4,0,0.2,1)]',
      'translate-y-0'
    );
  };

  // Scroll behavior

  const showHeader = () => {
    section.classList.remove(
      '-translate-y-full',
      'opacity-0',
      'pointer-events-none',
      'js-header-pdp-initial-hidden'
    );
    section.classList.add('translate-y-0');
  };

  const hideHeader = () => {
    section.classList.add('-translate-y-full');
    section.classList.remove('translate-y-0');
  };

  const fadeOutHeader = () => {
    section.classList.add('opacity-0', 'pointer-events-none');
    section.classList.remove('-translate-y-full');
    section.classList.add('translate-y-0');
  };

  const hidePdpHeaderBehindClone = () => {
    if (!isPdp || window.innerWidth < 1024) return;

    section.classList.add('opacity-0', 'pointer-events-none');
    section.classList.remove('-translate-y-full');
    section.classList.add('translate-y-0');
  };

  const isPdpHeaderCloneVisible = () => {
    if (!isPdp || window.innerWidth < 1024) return false;

    const clone = document.querySelector<HTMLElement>('.js-pdp-header-clone');
    const rect = clone?.getBoundingClientRect();

    return !!rect && rect.top >= 0 && rect.bottom <= window.innerHeight;
  };

  // On the PLP the filter bar is sticky and owns the top of the viewport (now at ALL breakpoints), so
  // the header must NOT reveal on scroll-up (it would cover the bar). It still reappears at the very
  // top of the page. Scoped to pages that actually render the sticky horizontal filter bar.
  const hasStickyFilterBar = !!document.querySelector('.facets-block-wrapper--horizontal');
  const suppressScrollUpReveal = () => hasStickyFilterBar;

  const handleHeaderScroll = () => {
    const current = window.scrollY;
    const goingUp = current < lastScroll;
    let isFixed = section.classList.contains('fixed');
  
    if (current <= 0) {
      section.classList.remove('fixed', 'bg-grey-800', '-translate-y-full', 'opacity-0', 'pointer-events-none');
      section.classList.add('translate-y-0');

      if (hasHero) {
        section.classList.add('absolute');
      } else {
        section.classList.remove('absolute');
        section.classList.add('relative');
      }

      hidePdpHeaderBehindClone();
  
      accumulatedUp = 0;
      lastScroll = current;
      return;
    }

    if (isPdp && isFixed && isPdpHeaderCloneVisible()) {
      fadeOutHeader();
      accumulatedUp = 0;
      lastScroll = current;
      return;
    }
      
    if (!isFixed && current > 200 ) {
      section.classList.add('fixed', 'bg-grey-800');
      section.classList.remove('absolute', 'relative');
      section.classList.remove('translate-y-0');
  
      section.style.transition = 'none';
      section.classList.add('-translate-y-full');
  
      section.getBoundingClientRect();
  
      section.style.transition = '';
  
      isFixed = true;
    }   
  
    if (goingUp) {
      accumulatedUp += lastScroll - current;

      if (isFixed && accumulatedUp > 80 && !suppressScrollUpReveal()) {
        showHeader();
      }
    } else {
      accumulatedUp = 0;
  
      if (isFixed) {
        hideHeader();
      }
    }
  
    lastScroll = current;
  };

  setInitialState();
  window.addEventListener('scroll', handleHeaderScroll, { passive: true });


  // Menu

  const menu = wrapper?.querySelector<HTMLElement>('.js-header-menu');
  const menuItems = menu?.querySelectorAll<HTMLElement>('.js-header-menu-item');
  const menuOverlay = menu?.querySelector<HTMLElement>('.js-header-menu-overlay');
  const menuPanel = menu?.querySelector<HTMLElement>('.js-header-menu-panel');
  const menuCloseTriggers = menu?.querySelectorAll<HTMLElement>('.js-header-menu-close');

  const menuScrollContainer = menuPanel?.querySelector<HTMLElement>('.js-header-menu-scroll');

  // Close icon that follows the cursor over the overlay (md+).
  const menuCloseFollow = menu?.querySelector<HTMLElement>('.js-drawer-close-follow') ?? null;
  const cursorClose = initCursorFollowClose(menuOverlay ?? null, menuCloseFollow);

  const openMenu = () => {
    if (!menuOverlay || !menuPanel || !menuCloseTriggers) return;

    if (menuScrollContainer) menuScrollContainer.scrollTop = 0;
    
    section.classList.add('opacity-0', 'pointer-events-none');
    menuOverlay.classList.remove('hidden', 'pointer-events-none');

    const headerClone = document.querySelector('.js-pdp-header-clone') as HTMLElement | null;
    headerClone?.classList.add('opacity-0', 'pointer-events-none');

    menuCloseTriggers.forEach((trigger) => {
      trigger.classList.remove('hidden');
    });

    requestAnimationFrame(() => {
      menuOverlay.classList.remove('opacity-0');
      menuPanel.classList.remove('translate-x-full');
      // Lock scroll on <body> not <html>: overflow-hidden on the root scroller breaks sticky descendants (e.g. the PDP variant selector). Matches the mini-bag.
      document.body.classList.add('overflow-hidden');
    });
  };

  const closeMenu = () => {
    if (!menuOverlay || !menuPanel || !menuCloseTriggers) return;
    
    section.classList.remove('opacity-0', 'pointer-events-none');
    menuOverlay.classList.add('opacity-0');
    menuPanel.classList.add('translate-x-full');

    cursorClose.hide();

    const headerClone = document.querySelector('.js-pdp-header-clone') as HTMLElement | null;
    headerClone?.classList.remove('opacity-0', 'pointer-events-none');

    document.body.classList.remove('overflow-hidden');

    setTimeout(() => {
      menuOverlay.classList.add('hidden', 'pointer-events-none');

      menuCloseTriggers.forEach((trigger) => {
        trigger.classList.add('hidden');
      });
    }, 400);
  };  

  const handleOpenMenuClick = (event: Event) => {
    const trigger = (event.target as HTMLElement).closest('.js-header-open-menu');
    if (trigger) openMenu();
  };

  document.addEventListener('click', handleOpenMenuClick);
  bindAll(menu, '.js-header-menu-close', 'click', closeMenu);
  menuOverlay?.addEventListener('click', closeMenu);

  const handleMenuKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      closeMenu();
    }
  };

  document.addEventListener('keydown', handleMenuKeydown);

  // Menu Hover (desktop)  
  
  const featuredItems = menu?.querySelectorAll<HTMLElement>('.js-header-menu-featured');

  const handleMenuItemMouseEnter = (event: Event) => {
    if (window.innerWidth < 1024) return;
    const item = event.currentTarget as HTMLElement;
    const featuredId = item.dataset.featured;

    menuItems?.forEach((menuItem) => {
      menuItem.classList.remove('text-text-primary');
      menuItem.classList.add('text-text-secondary');
    });
  
    item.classList.remove('text-text-secondary');
    item.classList.add('text-text-primary');

    featuredItems?.forEach((featured) => {
      const isActive = featured.dataset.featured === featuredId;

      featured.classList.toggle('opacity-100', isActive);
      featured.classList.toggle('opacity-0', !isActive);
    });
  };

  const handleMenuItemMouseLeave = (event: Event) => {
    if (window.innerWidth < 1024) return;    

    menuItems?.forEach((menuItem) => {
      menuItem.classList.add('text-text-primary');
      menuItem.classList.remove('text-text-secondary');
    });

    featuredItems?.forEach((featured) => {
      featured.classList.remove('opacity-100');
      featured.classList.add('opacity-0');
    });
  };

  bindAll(menu, '.js-header-menu-item', 'mouseenter', handleMenuItemMouseEnter);
  bindAll(menu, '.js-header-menu-item', 'mouseleave', handleMenuItemMouseLeave);

  return {
    unload() {
      stop();
      window.removeEventListener('touchstart', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      window.removeEventListener('resize', handleActivity);
      window.removeEventListener('scroll', handleHeaderScroll);
      document.removeEventListener('click', handleOpenMenuClick);
      document.removeEventListener('keydown', handleMenuKeydown);
      cursorClose.destroy();
    },

    select() {
      return () => {};
    }
  }; 
});
